import {
  memo,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import Api from '../../../api/Api';
import useStoreAccess from '../../../hooks/useStoreAccess';
import { useWatchAuthenticationState } from '../../../hooks/useWatchAuthenticationState';
import { useGetCurrentUserQuery } from '../../../queries/useGetCurrentUserQuery';
import Background from '../../layout/Background/Background.';
import RefreshConfirmationModal from './RefreshConfirmationModal';
import useCacheManager from './useCacheManager';

type Props = PropsWithChildren;

type ModalState = {
  showConfirmation: boolean;
};

function CacheSessionModal({ children }: Props) {
  useStoreAccess();

  const wasAuthenticated = useWatchAuthenticationState();
  const [isHardRedirecting, setIsHardRedirecting] = useState(false);
  const initialSearchRef = useRef(window.location.search);

  const { hasCache, clearCache } = useCacheManager();
  const { refetch } = useGetCurrentUserQuery();

  const navigate = useNavigate();

  const [modalState, setModalState] = useState<ModalState>({
    showConfirmation: true,
  });

  const isPaymentSuccessRoute = useMatch({
    path: '/paymentsuccess',
    end: true,
  });

  const { shouldShowSpinner } = useMemo(
    () => ({
      shouldShowSpinner: modalState.showConfirmation,
    }),
    [modalState.showConfirmation]
  );

  const consumePostLoginFlag = useCallback(() => {
    const key = 'skipCacheModalOnce';
    const has = sessionStorage.getItem(key) === '1';
    if (has) sessionStorage.removeItem(key);
    return has;
  }, []);

  const consumeWasSignedInBefore = useCallback(() => {
    const key = 'wasSignedInBefore';
    const val = sessionStorage.getItem(key) === '1';
    if (val) sessionStorage.removeItem(key);
    return val;
  }, []);

  const handleContinueSession = useCallback(async () => {
    setModalState({ showConfirmation: false });
    const wasSignedInBefore = consumeWasSignedInBefore();
    if (wasSignedInBefore && !wasAuthenticated) {
      setIsHardRedirecting(true);
      window.location.replace(
        `${Api.SERVER_DOMAIN}${
          Api.IISSite
        }/auth/signin?redirectUrl=${encodeURIComponent(window.location.origin)}`
      );
      return;
    } else {
      await refetch().catch(console.error);
      navigate('/');
    }
  }, [navigate, refetch, wasAuthenticated, consumeWasSignedInBefore]);

  const handleCancel = useCallback(() => {
    handleContinueSession();
  }, [handleContinueSession]);

  const continueSessionWithoutPrompt = useCallback(async () => {
    const wasSignedInBefore = consumeWasSignedInBefore();

    if (wasSignedInBefore && !wasAuthenticated) {
      window.location.replace(
        `${Api.SERVER_DOMAIN}${
          Api.IISSite
        }/auth/signin?redirectUrl=${encodeURIComponent(window.location.origin)}`
      );
      return;
    }

    await refetch().catch(console.error);
    navigate('/');
  }, [navigate, refetch, wasAuthenticated, consumeWasSignedInBefore]);

  const handleRefresh = useCallback(async () => {
    try {
      consumeWasSignedInBefore();
      await clearCache();
      setModalState({ showConfirmation: false });
      const targetPath = isPaymentSuccessRoute ? '/paymentsuccess' : '/';
      navigate(`${targetPath}${initialSearchRef.current}`);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }, [clearCache, navigate, isPaymentSuccessRoute, consumeWasSignedInBefore]);

  const refreshWithoutPrompt = useCallback(async () => {
    try {
      consumeWasSignedInBefore();
      await clearCache();
      const targetPath = isPaymentSuccessRoute ? '/paymentsuccess' : '/';
      navigate(`${targetPath}${initialSearchRef.current}`);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }, [clearCache, navigate, isPaymentSuccessRoute, consumeWasSignedInBefore]);

  useEffect(() => {
    if (isHardRedirecting) return;
    if (hasCache === false && consumeWasSignedInBefore()) return;
    if (hasCache === null) return;

    if (consumePostLoginFlag()) {
      void continueSessionWithoutPrompt();
    }
  }, [
    continueSessionWithoutPrompt,
    consumePostLoginFlag,
    consumeWasSignedInBefore,
    hasCache,
    isHardRedirecting,
  ]);

  useEffect(() => {
    if (isHardRedirecting) return;
    if (isPaymentSuccessRoute && hasCache) {
      void refreshWithoutPrompt();
    }
  }, [
    refreshWithoutPrompt,
    hasCache,
    isHardRedirecting,
    isPaymentSuccessRoute,
  ]);

  if (hasCache === null) {
    return <></>;
  }

  if (isHardRedirecting) {
    return <></>;
  }

  if (!hasCache) return <>{children}</>;

  if (shouldShowSpinner) {
    return (
      <>
        <RefreshConfirmationModal
          onCancel={handleCancel}
          onRefresh={handleRefresh}
        />
        <Background />
      </>
    );
  }

  return <>{children}</>;
}

export default memo(CacheSessionModal);
